import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  DASHBOARD_MID_WIDGET_HEIGHT,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetPaymentsOverviewResponse } from "subModule/src/services/analytics/interface";
import { useGetPaymentsOverviewService } from "subModule/src/services/analytics/useAnalytics";
import { TFunction } from "i18next";
import { PieChart } from "@mui/x-charts/PieChart";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
interface PaymentsOverviewProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
export default function PaymentsOverview(props: PaymentsOverviewProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const [data, setData] = useState<any>(null);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px",
  );
  const [loading, setLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetPaymentsOverviewService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetPaymentsOverviewResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };
  return (
    <Card
      sx={{
        height: DASHBOARD_MID_WIDGET_HEIGHT,
        minHeight: DASHBOARD_MID_WIDGET_HEIGHT,
        boxShadow: "none",
        borderRadius: "8px",
        "& .MuiCardContent-root": { padding: "16px !important" },
      }}
    >
      <CardContent>
        <Box sx={dashboardWidgetHeaderSx}>
          <AnalyticsWidgetHeader
            title={t("paymentStatusOverview")}
            icon={<AccountBalanceWalletOutlined />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                        { position: "absolute", right: 0 },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "paymentsStatus"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleDropdownActions(
                          "paymentsStatus",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleDropdownActions(
                            "paymentsStatus",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleDropdownActions(
                          "paymentsStatus",
                          "loadNext",
                        );
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: screenWidth ? "60px" : "auto",
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mt: 1,
              minHeight: 140,
            }}
          >
            <Skeleton
              variant="circular"
              width={130}
              height={130}
              sx={{ backgroundColor: props?.themes?.secondary, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton
                animation="pulse"
                sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
              />
              <Skeleton
                animation="pulse"
                sx={{ backgroundColor: props?.themes?.secondary }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              mt: 0.5,
              minHeight: 140,
            }}
          >
            <DemoPie data={data} themes={props.themes} {...{ t }} />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Tooltip
                followCursor
                title={data?.TotalPendingPayments?.toLocaleString() || 0}
                enterDelay={0}
              >
                <Box className="flex flex-col gap-0.5 w-[100%] max-w-[fit-content]">
                  <Box className="flex items-center gap-1">
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "2px",
                        backgroundColor: props.themes.primary,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      className="font-Saira"
                      sx={{ fontSize: 11, color: "#637381", fontWeight: 500 }}
                    >
                      {t("totalPendingPayments")}
                    </Typography>
                  </Box>
                  <Box className="flex items-baseline gap-1 pl-[14px]">
                    <span
                      className="font-Saira text-[14px] font-bold"
                      style={{ color: props.themes.primary }}
                    >
                      {new Intl.NumberFormat("en", {
                        notation: "compact",
                        compactDisplay: "short",
                        maximumFractionDigits: 2,
                      }).format(Math.abs(data?.TotalPendingPayments || 0))}
                    </span>
                    <span className="font-Saira uppercase text-[10px] font-medium text-[#919EAB]">
                      {props?.user?.business?.currency || ""}
                    </span>
                  </Box>
                </Box>
              </Tooltip>
              <Tooltip
                followCursor
                title={data?.TotalPaidPayments?.toLocaleString() || 0}
                enterDelay={0}
              >
                <Box className="flex flex-col gap-0.5 w-[100%] max-w-[fit-content]">
                  <Box className="flex items-center gap-1">
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "2px",
                        backgroundColor: props.themes.secondary,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      className="font-Saira"
                      sx={{ fontSize: 11, color: "#637381", fontWeight: 500 }}
                    >
                      {t("totalPaidPayments")}
                    </Typography>
                  </Box>
                  <Box className="flex items-baseline gap-1 pl-[14px]">
                    <span
                      className="font-Saira text-[14px] font-bold"
                      style={{ color: props.themes.secondary }}
                    >
                      {new Intl.NumberFormat("en", {
                        notation: "compact",
                        compactDisplay: "short",
                        maximumFractionDigits: 2,
                      }).format(Math.abs(data?.TotalPaidPayments || 0))}
                    </span>
                    <span className="font-Saira uppercase text-[10px] font-medium text-[#919EAB]">
                      {props?.user?.business?.currency || ""}
                    </span>
                  </Box>
                </Box>
              </Tooltip>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
const DemoPie = ({
  data,
  themes,
  t,
}: {
  data: any;
  themes?: any;
  t: TFunction<"translation", undefined>;
}) => {
  const allZero =
    Math.abs(data?.TotalPendingPayments || 0) +
      Math.abs(data?.TotalPaidPayments || 0) ===
    0;
  const mappedData = [
    {
      label: t("totalPaidPayments"),
      value: allZero ? 1 : Math.abs(data?.TotalPaidPayments),
      color: themes.secondary,
    },
    {
      label: t("totalPendingPayments"),
      value: allZero ? 1 : Math.abs(data?.TotalPendingPayments),
      color: themes.primary,
    },
  ];
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: 140,
        height: 140,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiChartsLegend-root": { display: "none" },
      }}
    >
      <PieChart
        height={140}
        width={140}
        series={[
          {
            data: mappedData,
            valueFormatter: (v) => {
              return `${allZero ? 0 : v.value?.toLocaleString() || 0}`;
            },
            innerRadius: 40,
            outerRadius: 62,
            paddingAngle: 1.5,
            cornerRadius: 3,
          },
        ]}
        skipAnimation={false}
        slotProps={{
          legend: { hidden: true },
        }}
        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
      />
    </Box>
  );
};
